/**
 * Entity Graph model (sections 23) — the pure, client-side engine that assembles
 * the team's Goal → Knowledge → Execution graph (plus a people layer) by
 * RESOLVING the cross-reference ids already stored on each entity. No backend,
 * no new collection: it folds the entities the app already persists into a
 * navigable node/edge graph.
 *
 * Shape of the graph (the "spine" the batch calls for):
 *
 *   OP1/OP2 → BHAG → Quarterly → Workstream → {Stories, Docs, Decisions}
 *            → Changes → Delivery → Outcome
 *
 * expressed as goal-hierarchy edges (parentGoalId), goal↔workstream edges
 * (workstream.goalId / goal.linkedInitiativeIds), workstream↔story edges
 * (sprintStoryIds), and the knowledge/execution links carried on documents,
 * decisions, changes, daily-updates, blockers and services. On top of that a
 * PEOPLE layer connects engineers to the work: owns Workstream, leads Goal,
 * works on Story, authors/owns/reviews Document, owns/participates in Decision,
 * is POC for a Service, follows a Workstream.
 *
 * This module is TOLERANT of its inputs: it reads full documents when available
 * (so it gets the id-arrays + contributors that live only in the S3 body) and
 * degrades gracefully to the lightweight pointer-row summaries (which already
 * carry most foreign keys) — so the same builder powers both the rich Graph
 * page (hydrated) and Ask Vector's retriever augmentation (summary-based).
 *
 * STRICT non-goal (section 28): NOTHING here scores, ranks, or measures a
 * person. People are nodes and connections only — "who is connected to this
 * work", never "how much / how well". No productivity, velocity, counts-as-rank.
 *
 * Pure TS — no React.
 */

import type { Goal, GoalSummary } from '../goals/types';
import type { Workstream, WorkstreamSummary } from '../workstreams/types';
import type { TeamDocumentSummary } from '../documents/types';
import type { DecisionSummary } from '../decisions/types';
import type { ServiceSummary } from '../services/types';
import type { EngineeringChangeSummary } from '../changes/types';
import type { DailyUpdateSummary } from '../dailyUpdates/types';
import type { BlockerSummary } from '../blockers/types';
import type { EntitySnapshot } from '../intelligence/types';

// ---------------------------------------------------------------------------
// Node / edge model
// ---------------------------------------------------------------------------

/** The kinds of node the graph carries (entity kinds + a people layer). */
export type GraphNodeKind =
  | 'goal'
  | 'workstream'
  | 'story'
  | 'document'
  | 'decision'
  | 'service'
  | 'change'
  | 'dailyUpdate'
  | 'blocker'
  | 'person';

/**
 * The semantic bucket an edge belongs to — used for the legend + colour, and to
 * separate the people layer from the goal/knowledge/execution spine.
 */
export type GraphEdgeLayer = 'goal' | 'knowledge' | 'execution' | 'people';

/** A single graph node. `id` is the source entity id, or a person alias/team. */
export interface GraphNode {
  /** Unique key: `${kind}:${id}` (person ids are lowercased aliases/teams). */
  key: string;
  kind: GraphNodeKind;
  /** Source entity id, or the alias/team string for a person node. */
  id: string;
  /** Display title/headline. */
  title: string;
  /** Optional deep-link route into the SPA (undefined for routeless kinds). */
  route?: string;
  /** Optional one-line subtitle (status / stage / role hint). */
  subtitle?: string;
  /** True when a person node represents a team rather than an individual. */
  isTeam?: boolean;
}

/** A relationship between two nodes, carrying a human label + its layer. */
export interface GraphEdge {
  /** Source node key. */
  from: string;
  /** Target node key. */
  to: string;
  /** Human-readable relationship label, read `from → to`. */
  label: string;
  /** Which layer of the graph this edge belongs to. */
  layer: GraphEdgeLayer;
}

/** The assembled graph: node map + edge list + undirected adjacency. */
export interface EntityGraph {
  nodes: Map<string, GraphNode>;
  edges: GraphEdge[];
  /** Undirected adjacency: node key → the edges touching it. */
  adjacency: Map<string, GraphEdge[]>;
}

// ---------------------------------------------------------------------------
// Tolerant input shapes (accept full docs OR pointer-row summaries)
// ---------------------------------------------------------------------------

/** Goal fields the graph can use (all optional beyond id/title). */
export interface GraphGoalInput {
  id: string;
  title: string;
  status?: string;
  framework?: string;
  parentGoalId?: string;
  relatedGoalIds?: string[];
  linkedStoryIds?: string[];
  linkedInitiativeIds?: string[];
  goalLead?: string;
  contributors?: string[];
  pocs?: string[];
  leadershipSponsor?: string;
  owningTeam?: string;
}

/** Workstream fields the graph can use. */
export interface GraphWorkstreamInput {
  id: string;
  title: string;
  currentStage?: string;
  engineer?: string;
  poc?: string;
  followers?: string[];
  goalId?: string;
  sprintStoryIds?: string[];
  systemsAffected?: string[];
}

/** Story (task) fields the graph can use. */
export interface GraphStoryInput {
  id: string;
  title: string;
  status?: string;
  assignee?: string;
}

/** The normalized input the builder folds into a graph. */
export interface GraphSource {
  goals: GraphGoalInput[];
  workstreams: GraphWorkstreamInput[];
  stories: GraphStoryInput[];
  documents: TeamDocumentSummary[];
  decisions: DecisionSummary[];
  services: ServiceSummary[];
  changes: EngineeringChangeSummary[];
  dailyUpdates: DailyUpdateSummary[];
  blockers: BlockerSummary[];
}

// ---------------------------------------------------------------------------
// Adapters — build a GraphSource from full docs or from a snapshot
// ---------------------------------------------------------------------------

/** Adapts a full Goal document to graph input (keeps every foreign key). */
export function graphGoalFromDoc(g: Goal): GraphGoalInput {
  return {
    id: g.id,
    title: g.title,
    status: g.status,
    framework: g.goalFramework,
    parentGoalId: g.parentGoalId,
    relatedGoalIds: g.relatedGoalIds,
    linkedStoryIds: g.linkedStoryIds,
    linkedInitiativeIds: g.linkedInitiativeIds,
    goalLead: g.goalLead,
    contributors: g.contributors,
    pocs: g.pocs,
    leadershipSponsor: g.leadershipSponsor,
    owningTeam: g.owningTeam,
  };
}

/** Adapts a Goal pointer-row summary to graph input (no id-arrays/contributors). */
export function graphGoalFromSummary(g: GoalSummary): GraphGoalInput {
  return {
    id: g.id,
    title: g.title,
    status: g.status,
    framework: g.goalFramework,
    parentGoalId: g.parentGoalId,
    goalLead: g.goalLead,
    leadershipSponsor: g.leadershipSponsor,
    owningTeam: g.owningTeam,
  };
}

/** Adapts a full Workstream document to graph input. */
export function graphWorkstreamFromDoc(w: Workstream): GraphWorkstreamInput {
  return {
    id: w.id,
    title: w.title,
    currentStage: w.currentStage,
    engineer: w.engineer,
    poc: w.poc,
    followers: w.followers,
    goalId: w.goalId,
    sprintStoryIds: w.sprintStoryIds,
    systemsAffected: w.systemsAffected,
  };
}

/** Adapts a Workstream pointer-row summary to graph input (no sprintStoryIds). */
export function graphWorkstreamFromSummary(
  w: WorkstreamSummary,
): GraphWorkstreamInput {
  return {
    id: w.id,
    title: w.title,
    currentStage: w.currentStage,
    engineer: w.engineer,
    poc: w.poc,
    followers: w.followers,
    goalId: w.goalId,
    systemsAffected: w.systemsAffected,
  };
}

/**
 * Builds a (summary-based) GraphSource from an intelligence EntitySnapshot. Used
 * by Ask Vector's retriever to reason over connections without hydrating full
 * documents. Goal story-arrays / workstream sprint-story-arrays are absent at
 * this fidelity — the reverse links carried on docs/decisions/changes/updates/
 * blockers still connect stories to the spine.
 */
export function graphSourceFromSnapshot(snapshot: EntitySnapshot): GraphSource {
  return {
    goals: snapshot.goals.map(graphGoalFromSummary),
    workstreams: snapshot.workstreams.map(graphWorkstreamFromSummary),
    stories: snapshot.tasks.map((t) => ({
      id: t.id,
      title: t.title,
      status: t.status,
      assignee: t.assignee,
    })),
    documents: snapshot.documents,
    decisions: snapshot.decisions,
    services: snapshot.services,
    changes: snapshot.changes,
    dailyUpdates: snapshot.dailyUpdates,
    blockers: snapshot.blockers,
  };
}

// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

/** Person node key from an alias/team (case-insensitive identity). */
function personKey(alias: string): string {
  return `person:${alias.trim().toLowerCase()}`;
}

const enc = (id: string) => encodeURIComponent(id);

/** Route for an entity node kind (routeless kinds return undefined). */
function routeFor(kind: GraphNodeKind, id: string): string | undefined {
  switch (kind) {
    case 'goal':
      return `/goals/${enc(id)}`;
    case 'workstream':
      return `/workstreams/${enc(id)}`;
    case 'document':
      return `/documents/${enc(id)}`;
    case 'decision':
      return `/decisions/${enc(id)}`;
    case 'service':
      return `/services/${enc(id)}`;
    case 'story':
      return '/tasks';
    case 'change':
      return '/changes';
    case 'dailyUpdate':
      return '/daily-updates';
    case 'blocker':
      return '/blockers';
    case 'person':
    default:
      return undefined;
  }
}

/**
 * Assembles the {@link EntityGraph} from a {@link GraphSource}. Every edge is
 * de-duplicated on (from, to, label). Person/team nodes are created lazily as
 * they are referenced. Edges are only created between nodes that exist, so a
 * dangling id (e.g. a story referenced that is not in scope) simply produces no
 * edge rather than a phantom node.
 */
export function buildEntityGraph(source: GraphSource): EntityGraph {
  const nodes = new Map<string, GraphNode>();
  const edges: GraphEdge[] = [];
  const edgeSeen = new Set<string>();

  const upsertEntity = (
    kind: GraphNodeKind,
    id: string,
    title: string,
    subtitle?: string,
  ): string | undefined => {
    const clean = (id ?? '').trim();
    if (!clean) return undefined;
    const key = `${kind}:${clean}`;
    if (!nodes.has(key)) {
      nodes.set(key, {
        key,
        kind,
        id: clean,
        title: title?.trim() || clean,
        route: routeFor(kind, clean),
        subtitle,
      });
    }
    return key;
  };

  const upsertPerson = (alias: string, isTeam = false): string | undefined => {
    const clean = (alias ?? '').trim();
    if (!clean) return undefined;
    const key = personKey(clean);
    if (!nodes.has(key)) {
      nodes.set(key, {
        key,
        kind: 'person',
        id: clean,
        title: clean,
        subtitle: isTeam ? 'Team' : undefined,
        isTeam,
      });
    }
    return key;
  };

  const link = (
    from: string | undefined,
    to: string | undefined,
    label: string,
    layer: GraphEdgeLayer,
  ) => {
    if (!from || !to || from === to) return;
    // Only connect nodes that actually exist in scope.
    if (!nodes.has(from) || !nodes.has(to)) return;
    const sig = `${from}|${to}|${label}`;
    if (edgeSeen.has(sig)) return;
    edgeSeen.add(sig);
    edges.push({ from, to, label, layer });
  };

  // --- Pass 1: create all entity nodes so links can find their targets -----
  const goalKey = new Map<string, string>();
  for (const g of source.goals) {
    const key = upsertEntity(
      'goal',
      g.id,
      g.title,
      [g.framework, g.status].filter(Boolean).join(' · ') || undefined,
    );
    if (key) goalKey.set(g.id, key);
  }
  const wsKey = new Map<string, string>();
  for (const w of source.workstreams) {
    const key = upsertEntity('workstream', w.id, w.title, w.currentStage);
    if (key) wsKey.set(w.id, key);
  }
  const storyKey = new Map<string, string>();
  for (const t of source.stories) {
    const key = upsertEntity('story', t.id, t.title, t.status);
    if (key) storyKey.set(t.id, key);
  }
  const docKey = new Map<string, string>();
  for (const d of source.documents) {
    const key = upsertEntity('document', d.id, d.title, d.docType);
    if (key) docKey.set(d.id, key);
  }
  const decKey = new Map<string, string>();
  for (const d of source.decisions) {
    const key = upsertEntity('decision', d.id, d.decision);
    if (key) decKey.set(d.id, key);
  }
  const svcKey = new Map<string, string>();
  const svcByName = new Map<string, string>();
  for (const s of source.services) {
    const key = upsertEntity('service', s.id, s.name, s.owningTeam);
    if (key) {
      svcKey.set(s.id, key);
      if (s.name) svcByName.set(s.name.trim().toLowerCase(), key);
    }
  }
  for (const c of source.changes) {
    upsertEntity('change', c.id, c.title, c.engineer);
  }
  for (const u of source.dailyUpdates) {
    upsertEntity('dailyUpdate', u.id, `${u.engineer} — ${u.date}`);
  }
  for (const b of source.blockers) {
    upsertEntity('blocker', b.id, b.summary, b.status);
  }

  /** Resolves a service reference token (id first, then by name). */
  const resolveService = (token: string): string | undefined => {
    const clean = (token ?? '').trim();
    if (!clean) return undefined;
    return svcKey.get(clean) ?? svcByName.get(clean.toLowerCase());
  };

  // --- Pass 2: goal spine + people on goals --------------------------------
  for (const g of source.goals) {
    const key = goalKey.get(g.id);
    if (!key) continue;
    // Hierarchy: parent → this goal.
    if (g.parentGoalId) {
      link(goalKey.get(g.parentGoalId), key, 'sub-goal', 'goal');
    }
    for (const r of g.relatedGoalIds ?? []) {
      link(key, goalKey.get(r), 'related goal', 'goal');
    }
    // Goal → workstream (initiatives) + goal → story (linked work).
    for (const iid of g.linkedInitiativeIds ?? []) {
      link(key, wsKey.get(iid), 'workstream', 'execution');
    }
    for (const sid of g.linkedStoryIds ?? []) {
      link(key, storyKey.get(sid), 'story', 'execution');
    }
    // People on the goal.
    link(upsertPerson(g.goalLead ?? ''), key, 'leads', 'people');
    for (const c of g.contributors ?? []) link(upsertPerson(c), key, 'contributes to', 'people');
    for (const p of g.pocs ?? []) link(upsertPerson(p), key, 'POC for', 'people');
    link(upsertPerson(g.leadershipSponsor ?? ''), key, 'sponsors', 'people');
    link(upsertPerson(g.owningTeam ?? '', true), key, 'owning team of', 'people');
  }

  // --- Pass 3: workstreams (spine + systems + people) ----------------------
  for (const w of source.workstreams) {
    const key = wsKey.get(w.id);
    if (!key) continue;
    link(goalKey.get(w.goalId ?? ''), key, 'advanced by', 'goal');
    for (const sid of w.sprintStoryIds ?? []) {
      link(key, storyKey.get(sid), 'story', 'execution');
    }
    for (const sys of w.systemsAffected ?? []) {
      link(key, resolveService(sys), 'affects service', 'execution');
    }
    link(upsertPerson(w.engineer ?? ''), key, 'owns', 'people');
    link(upsertPerson(w.poc ?? ''), key, 'POC for', 'people');
    for (const f of w.followers ?? []) link(upsertPerson(f), key, 'follows', 'people');
  }

  // --- Pass 4: stories (people) --------------------------------------------
  for (const t of source.stories) {
    const key = storyKey.get(t.id);
    if (!key) continue;
    link(upsertPerson(t.assignee ?? ''), key, 'works on', 'people');
  }

  // --- Pass 5: documents (knowledge links + people) -----------------------
  for (const d of source.documents) {
    const key = docKey.get(d.id);
    if (!key) continue;
    link(key, goalKey.get(d.relatedGoalId ?? ''), 'documents goal', 'knowledge');
    link(key, wsKey.get(d.relatedWorkstreamId ?? ''), 'documents workstream', 'knowledge');
    for (const sid of d.relatedStoryIds ?? []) {
      link(key, storyKey.get(sid), 'documents story', 'knowledge');
    }
    for (const did of d.relatedDecisionIds ?? []) {
      link(key, decKey.get(did), 'relates to decision', 'knowledge');
    }
    for (const s of d.relatedServices ?? []) {
      link(key, resolveService(s), 'documents service', 'knowledge');
    }
    if (d.supersedesId) link(key, docKey.get(d.supersedesId), 'supersedes', 'knowledge');
    for (const a of d.authors ?? []) link(upsertPerson(a), key, 'authored', 'people');
    link(upsertPerson(d.ownerPoc ?? ''), key, 'owns', 'people');
    for (const r of d.reviewers ?? []) link(upsertPerson(r), key, 'reviewed', 'people');
  }

  // --- Pass 6: decisions (knowledge links + people) -----------------------
  for (const d of source.decisions) {
    const key = decKey.get(d.id);
    if (!key) continue;
    link(key, goalKey.get(d.relatedGoalId ?? ''), 'decides for goal', 'knowledge');
    link(key, wsKey.get(d.relatedWorkstreamId ?? ''), 'decides for workstream', 'knowledge');
    link(key, docKey.get(d.relatedDocId ?? ''), 'recorded in doc', 'knowledge');
    for (const sid of d.relatedStoryIds ?? []) {
      link(key, storyKey.get(sid), 'decides for story', 'knowledge');
    }
    for (const s of d.affectedServices ?? []) {
      link(key, resolveService(s), 'affects service', 'knowledge');
    }
    link(upsertPerson(d.owner ?? ''), key, 'owns', 'people');
    for (const p of d.participants ?? []) link(upsertPerson(p), key, 'participated in', 'people');
  }

  // --- Pass 7: changes (execution links + people) -------------------------
  for (const c of source.changes) {
    const key = `change:${c.id}`;
    if (!nodes.has(key)) continue;
    link(key, goalKey.get(c.goalId ?? ''), 'change for goal', 'execution');
    link(key, wsKey.get(c.workstreamId ?? ''), 'change in workstream', 'execution');
    link(upsertPerson(c.engineer ?? ''), key, 'made', 'people');
  }

  // --- Pass 8: daily updates (execution links + people) -------------------
  for (const u of source.dailyUpdates) {
    const key = `dailyUpdate:${u.id}`;
    if (!nodes.has(key)) continue;
    link(key, goalKey.get(u.goalId ?? ''), 'update on goal', 'execution');
    link(key, wsKey.get(u.workstreamId ?? ''), 'update on workstream', 'execution');
    link(key, storyKey.get(u.storyId ?? ''), 'update on story', 'execution');
    link(upsertPerson(u.engineer ?? ''), key, 'posted', 'people');
  }

  // --- Pass 9: blockers (execution links + people) ------------------------
  for (const b of source.blockers) {
    const key = `blocker:${b.id}`;
    if (!nodes.has(key)) continue;
    link(key, goalKey.get(b.affectedGoalId ?? ''), 'blocks goal', 'execution');
    link(key, wsKey.get(b.blockedWorkstreamId ?? ''), 'blocks workstream', 'execution');
    link(key, storyKey.get(b.blockedStoryId ?? ''), 'blocks story', 'execution');
    link(upsertPerson(b.blockedEngineer ?? ''), key, 'blocked on', 'people');
    link(upsertPerson(b.dependencyOwner ?? ''), key, 'owns dependency for', 'people');
  }

  // --- Pass 10: services (link to goals/workstreams/docs/decisions + POC) --
  for (const s of source.services) {
    const key = svcKey.get(s.id);
    if (!key) continue;
    for (const gid of s.currentGoalIds ?? []) {
      link(key, goalKey.get(gid), 'advances goal', 'knowledge');
    }
    for (const wid of s.activeWorkstreamIds ?? []) {
      link(key, wsKey.get(wid), 'touched by workstream', 'knowledge');
    }
    for (const did of s.documentIds ?? []) {
      link(key, docKey.get(did), 'described by doc', 'knowledge');
    }
    for (const rid of s.recentDecisionIds ?? []) {
      link(key, decKey.get(rid), 'decision about', 'knowledge');
    }
    link(upsertPerson(s.primaryPoc ?? ''), key, 'POC for', 'people');
    if (s.secondaryPoc) link(upsertPerson(s.secondaryPoc), key, 'backup POC for', 'people');
    link(upsertPerson(s.owningTeam ?? '', true), key, 'owns', 'people');
  }

  // --- Build undirected adjacency -----------------------------------------
  const adjacency = new Map<string, GraphEdge[]>();
  for (const e of edges) {
    (adjacency.get(e.from) ?? adjacency.set(e.from, []).get(e.from)!).push(e);
    (adjacency.get(e.to) ?? adjacency.set(e.to, []).get(e.to)!).push(e);
  }

  return { nodes, edges, adjacency };
}

// ---------------------------------------------------------------------------
// Traversal / selection
// ---------------------------------------------------------------------------

/** A node paired with its BFS depth from a chosen root. */
export interface GraphNodeAtDepth {
  node: GraphNode;
  depth: number;
}

/** The connected subgraph around a root: its nodes (with depth) + inner edges. */
export interface ConnectedSubgraph {
  root: GraphNode;
  /** All reached nodes, including the root (depth 0), sorted by depth. */
  nodes: GraphNodeAtDepth[];
  /** Edges whose endpoints are both within the reached set. */
  edges: GraphEdge[];
  /** Depth lookup by node key. */
  depthByKey: Map<string, number>;
}

/**
 * BFS the undirected graph from `rootKey` up to `maxDepth` hops, returning the
 * connected subgraph (reached nodes + the edges among them). Returns undefined
 * when the root key is not in the graph.
 */
export function connectedSubgraph(
  graph: EntityGraph,
  rootKey: string,
  maxDepth = 2,
): ConnectedSubgraph | undefined {
  const root = graph.nodes.get(rootKey);
  if (!root) return undefined;

  const depthByKey = new Map<string, number>([[rootKey, 0]]);
  const queue: string[] = [rootKey];
  while (queue.length > 0) {
    const key = queue.shift()!;
    const depth = depthByKey.get(key) ?? 0;
    if (depth >= maxDepth) continue;
    for (const edge of graph.adjacency.get(key) ?? []) {
      const next = edge.from === key ? edge.to : edge.from;
      if (depthByKey.has(next)) continue;
      depthByKey.set(next, depth + 1);
      queue.push(next);
    }
  }

  const reached = new Set(depthByKey.keys());
  const nodes: GraphNodeAtDepth[] = [];
  for (const [key, depth] of depthByKey) {
    const node = graph.nodes.get(key);
    if (node) nodes.push({ node, depth });
  }
  nodes.sort(
    (a, b) => a.depth - b.depth || a.node.title.localeCompare(b.node.title),
  );

  const edges = graph.edges.filter(
    (e) => reached.has(e.from) && reached.has(e.to),
  );

  return { root, nodes, edges, depthByKey };
}

/** Edges touching a node, each oriented as {neighbor, edge, outward}. */
export interface NeighborEdge {
  neighbor: GraphNode;
  edge: GraphEdge;
  /** True when the edge points FROM this node TO the neighbor. */
  outward: boolean;
}

/** All immediate neighbors of a node (with the connecting edge + direction). */
export function neighborsOf(graph: EntityGraph, key: string): NeighborEdge[] {
  const out: NeighborEdge[] = [];
  for (const edge of graph.adjacency.get(key) ?? []) {
    const outward = edge.from === key;
    const neighborKey = outward ? edge.to : edge.from;
    const neighbor = graph.nodes.get(neighborKey);
    if (neighbor) out.push({ neighbor, edge, outward });
  }
  return out;
}

/**
 * The nodes eligible to be a Graph-page root: goals, workstreams and services
 * (the "start here" anchors), sorted by kind then title. Isolated nodes (no
 * edges) are excluded — there is nothing to explore from them.
 */
export function graphRoots(graph: EntityGraph): GraphNode[] {
  const rootKinds: GraphNodeKind[] = ['goal', 'workstream', 'service'];
  const order: Record<string, number> = { goal: 0, workstream: 1, service: 2 };
  return [...graph.nodes.values()]
    .filter(
      (n) =>
        rootKinds.includes(n.kind) && (graph.adjacency.get(n.key)?.length ?? 0) > 0,
    )
    .sort(
      (a, b) =>
        (order[a.kind] ?? 9) - (order[b.kind] ?? 9) ||
        a.title.localeCompare(b.title),
    );
}

// ---------------------------------------------------------------------------
// Ask Vector retriever augmentation
// ---------------------------------------------------------------------------

/** Human label for a node kind (used in the relationships context block). */
export function nodeKindLabel(kind: GraphNodeKind): string {
  switch (kind) {
    case 'goal':
      return 'Goal';
    case 'workstream':
      return 'Workstream';
    case 'story':
      return 'Story';
    case 'document':
      return 'Document';
    case 'decision':
      return 'Decision';
    case 'service':
      return 'Service';
    case 'change':
      return 'Change';
    case 'dailyUpdate':
      return 'Daily Update';
    case 'blocker':
      return 'Blocker';
    case 'person':
      return 'Person';
  }
}

/**
 * Formats a compact RELATIONSHIPS block describing how a set of in-scope
 * entities (identified by their source ids) connect to their immediate
 * neighbors. Fed to Ask Vector alongside the retrieved entity context so the
 * model can reason about connections ("who owns X", "what does Y affect")
 * instead of treating each entity in isolation.
 *
 * Bounded per entity + overall so the prompt stays small. Awareness only — it
 * lists connections, never counts or ranks.
 */
export function formatRelationships(
  graph: EntityGraph,
  entityIds: string[],
  options: { maxEntities?: number; maxPerEntity?: number } = {},
): string {
  const maxEntities = options.maxEntities ?? 16;
  const maxPerEntity = options.maxPerEntity ?? 8;

  // Resolve ids to node keys (an id may exist under several kinds; take all).
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const node of graph.nodes.values()) {
    if (node.kind === 'person') continue;
    if (!entityIds.includes(node.id)) continue;
    if (seen.has(node.key)) continue;
    seen.add(node.key);
    keys.push(node.key);
  }
  if (keys.length === 0) return '';

  const lines: string[] = [];
  for (const key of keys.slice(0, maxEntities)) {
    const node = graph.nodes.get(key);
    if (!node) continue;
    const neighbors = neighborsOf(graph, key);
    if (neighbors.length === 0) continue;
    const parts = neighbors.slice(0, maxPerEntity).map(({ neighbor, edge, outward }) => {
      const rel = outward ? edge.label : `${edge.label} ←`;
      return `${rel} ${nodeKindLabel(neighbor.kind)} "${neighbor.title}"`;
    });
    const more = neighbors.length > maxPerEntity ? ` (+${neighbors.length - maxPerEntity} more)` : '';
    lines.push(`- ${nodeKindLabel(node.kind)} "${node.title}": ${parts.join('; ')}${more}`);
  }
  if (lines.length === 0) return '';
  return ['RELATIONSHIPS (how the retrieved entities connect):', ...lines].join('\n');
}
